import { useState, useEffect } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useUsageLog, type UsageLogItem } from '../../hooks/useUsage';
import { fmt, fmtCost, fmtDate, shortId } from '../../utils/format';

function LogRow({ row }: { row: UsageLogItem }) {
  return (
    <tr className="border-b border-app-border/30 last:border-0 hover:bg-app-muted">
      <td className="py-2.5 pr-3 text-xs text-app-text-dim whitespace-nowrap">
        {row.created_at ? fmtDate(row.created_at) : '—'}
      </td>
      <td className="py-2.5 pr-3 text-xs font-mono text-app-text" title={row.task_id}>
        {shortId(row.task_id, 10)}
      </td>
      <td className="py-2.5 pr-3 text-xs text-app-text max-w-[140px] truncate" title={row.node}>
        {row.node}
      </td>
      <td className="py-2.5 pr-3 text-xs font-mono text-app-text-dim max-w-[180px] truncate" title={row.model_name}>
        {row.model_name}
      </td>
      <td className="py-2.5 pr-3 text-xs text-app-text text-right whitespace-nowrap">{fmt(row.input_tokens)}</td>
      <td className="py-2.5 pr-3 text-xs text-app-text text-right whitespace-nowrap">{fmt(row.output_tokens)}</td>
      <td className="py-2.5 pr-3 text-xs text-app-text text-right font-medium whitespace-nowrap">
        {fmtCost(row.cost_usd)}
      </td>
      <td className="py-2.5 pr-3 text-xs text-app-text max-w-[100px] truncate" title={row.org_name || ''}>
        {row.org_name ?? '—'}
      </td>
      <td className="py-2.5 text-xs text-app-text-dim max-w-[100px] truncate" title={row.user_sub || ''}>
        {row.user_sub ?? '—'}
      </td>
    </tr>
  );
}

const DAYS_OPTIONS = [1, 7, 14, 30];

export default function LogTab() {
  const [days, setDays] = useState(7);
  const [orgFilter, setOrgFilter] = useState('');
  const [page, setPage] = useState(0);
  const { data, loading, error, refresh, pageSize } = useUsageLog(days, orgFilter, page);

  useEffect(() => {
    setPage(0);
  }, [days, orgFilter]);

  const totalPages =
    data && !data.error && data.total > 0 ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-app-text-dim mr-2">Period:</p>
          {DAYS_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                days === d
                  ? 'bg-app-accent/10 border-app-accent text-app-accent font-medium'
                  : 'border-app-border text-app-text-dim hover:bg-app-muted'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-app-text-dim shrink-0" htmlFor="usage-log-org">
            Org:
          </label>
          <input
            id="usage-log-org"
            type="text"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            placeholder="filter (optional)"
            className="min-w-[8rem] max-w-xs rounded-lg border border-app-border bg-app-surface px-3 py-1.5 text-sm text-app-text"
          />
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-app-accent border border-app-border hover:bg-app-muted disabled:opacity-50 self-start"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <p className="text-xs text-app-text-dim">
        ประวัติแต่ละครั้งที่เรียก LLM จากตาราง <span className="font-mono">llm_usage_log</span> ใน PostgreSQL
        (สูงสุด {pageSize} แถวต่อหน้า)
      </p>

      {loading && !data && (
        <p className="text-sm text-app-text-dim py-8 text-center">Loading...</p>
      )}

      {error && (
        <div className="bg-app-error/10 border border-app-error/30 rounded-xl p-4 text-sm text-app-error">
          {error}
        </div>
      )}

      {data?.error && (
        <div className="bg-app-error/10 border border-app-error/30 rounded-xl p-4 text-sm text-app-error">
          {data.error}
        </div>
      )}

      {data && !data.error && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-app-text-dim">
            <span>
              ทั้งหมด <span className="font-medium text-app-text">{fmt(data.total)}</span> รายการ
              {data.filter_org_name ? (
                <>
                  {' '}
                  · กรอง org <span className="font-mono text-app-accent">{data.filter_org_name}</span>
                </>
              ) : null}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="px-2 py-1 rounded border border-app-border text-app-text disabled:opacity-40"
              >
                Prev
              </button>
              <span className="text-app-text tabular-nums">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => setPage((p) => p + 1)}
                className="px-2 py-1 rounded border border-app-border text-app-text disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>

          <div className="bg-app-surface border border-app-border rounded-xl overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead>
                <tr className="border-b border-app-border text-[10px] text-app-text-dim uppercase tracking-wider">
                  <th className="py-2.5 pr-3 text-left font-medium">Time</th>
                  <th className="py-2.5 pr-3 text-left font-medium">Task</th>
                  <th className="py-2.5 pr-3 text-left font-medium">Node</th>
                  <th className="py-2.5 pr-3 text-left font-medium">Model</th>
                  <th className="py-2.5 pr-3 text-right font-medium">In</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Out</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Cost</th>
                  <th className="py-2.5 pr-3 text-left font-medium">Org</th>
                  <th className="py-2.5 text-left font-medium">User</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-sm text-app-text-dim">
                      ไม่มีรายการในช่วงนี้
                    </td>
                  </tr>
                ) : (
                  data.items.map((row) => <LogRow key={row.id} row={row} />)
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
